import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LogOut, KeyRound, Shield, Search, Bell, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { useState, useRef, useEffect } from "react";
import { buildNavGroups } from "./navConfig";
import { SidebarNav } from "./SidebarNav";
import { canAccessPage, isFreelanceRole } from "@/lib/rbac";
import { useOpsNotifications } from "@/hooks/useOpsNotifications";
import { CommandPalette } from "@/components/CommandPalette";
import { RouteErrorBoundary } from "@/components/layout/RouteErrorBoundary";

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [notifOpen, setNotifOpen] = useState(false);
  // Lot A : sidebar repliable sur mobile (les SDR appellent depuis leur téléphone).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => setMobileNavOpen(false), [location.pathname]);
  const notifRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markAllRead } = useOpsNotifications();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    if (notifOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // ── Badges du shell (Lot 2, closure #328) ────────────────────────────────
  // AdminLayout est monté sur TOUTES les pages : chaque badge est gaté par la
  // page métier qu'il représente (canAccessPage), sinon un rôle sans la page
  // prenait des 403 de fond sur tout le backoffice (SALES : goQueue/artisans/
  // SLA/trials/fraude ; SUPPORT : goQueue/trials/fraude). Le backend n'est
  // PAS élargi : la query ne part simplement jamais, badge absent, zéro 403.
  const role = user?.staffRole;
  const canSeeGoQueueBadge = canAccessPage(role, "GO_QUEUE");
  const canSeeMessagesBadge = canAccessPage(role, "MESSAGES");
  const canSeeSupportBadge = canAccessPage(role, "SUPPORT");
  const canSeeArtisansBadge = canAccessPage(role, "ARTISANS_MODERATION");
  const canSeeSlaBadge = canAccessPage(role, "SLA_LEADS");
  const canSeeTrialAlertsBadge = canAccessPage(role, "TRIAL_ALERTS");
  const canSeeFraudBadge = canAccessPage(role, "FRAUD_ALERTS");

  const { data: goQueueCount } = trpc.admin.dashboard.goQueue.useQuery(
    { limit: 100 },
    { enabled: canSeeGoQueueBadge }
  );
  const goQueueBadge = goQueueCount?.length || 0;

  // Unread messages count
  const { data: unreadMessagesData } = trpc.messaging.getUnreadCount.useQuery(undefined, {
    enabled: canSeeMessagesBadge,
  });
  const unreadMsgBadge = unreadMessagesData?.count || 0;

  // Open support tickets count
  const { data: supportStats } = trpc.admin.support.stats.useQuery(undefined, {
    retry: false,
    enabled: canSeeSupportBadge,
  });
  const supportBadge = supportStats?.totalOpen || 0;

  // Pending artisan verifications badge
  const { data: pendingArtisansData } = trpc.admin.travauxbtp.listPendingArtisans.useQuery(
    { page: 1, limit: 1 },
    { retry: false, enabled: canSeeArtisansBadge }
  );
  const artisansBadge = pendingArtisansData?.total || 0;

  // Urgent SLA leads badge (<12h remaining)
  const { data: slaLeadsData } = trpc.admin.leadSla.getLeadSLAStatus.useQuery(
    { page: 1, limit: 100 },
    { retry: false, refetchInterval: 60_000, enabled: canSeeSlaBadge }
  );
  const slaUrgentBadge = (slaLeadsData?.items ?? []).filter((item) => {
    if (!item.refundEligibleAt) return false;
    const ms = new Date(item.refundEligibleAt).getTime() - Date.now();
    return ms > 0 && ms < 12 * 60 * 60 * 1000;
  }).length;

  // Urgent trial alerts badge (CRITICAL J-3 + EXPIRED J-0)
  // Source: getTrialAlerts (admin.companies.list n'expose pas planExpiresAt).
  const { data: trialAlertsData } = trpc.admin.trialAlerts.getTrialAlerts.useQuery(
    undefined,
    { retry: false, refetchInterval: 120_000, enabled: canSeeTrialAlertsBadge }
  );
  const trialAlertsBadge = new Set(
    (trialAlertsData ?? [])
      .filter((a: any) => a.severity === "CRITICAL" || a.severity === "EXPIRED")
      .map((a: any) => a.companyId)
  ).size;

  // Active fraud alerts badge
  const { data: fraudAlertsData } = trpc.admin.fraudAlerts.listActive.useQuery(undefined, {
    retry: false,
    refetchInterval: 60_000,
    enabled: canSeeFraudBadge,
  });
  const fraudAlertsBadge = fraudAlertsData?.length || 0;

  // Navigation : groupes et entrées dans navConfig.ts (une seule source),
  // état du menu (repli, scroll) dans SidebarNav — il survit au remontage
  // d'AdminLayout par chaque page.
  const isFreelance = isFreelanceRole(user?.staffRole);
  // Espace freelance pro : badge des notifications non lues (freelances seulement).
  const canSeeFreelanceNotifBadge = canAccessPage(role, "PROSPECTION_NOTIFICATIONS") && isFreelance;
  const { data: freelanceNotif } = trpc.prospection.notifications.useQuery(
    { limit: 1 },
    { enabled: canSeeFreelanceNotifBadge, refetchInterval: 60_000 }
  );
  const navGroups = buildNavGroups(user?.staffRole, {
    freelanceNotifications: freelanceNotif?.unread ?? 0,
    goQueue: goQueueBadge,
    unreadMessages: unreadMsgBadge,
    support: supportBadge,
    artisans: artisansBadge,
    slaUrgent: slaUrgentBadge,
    trialAlerts: trialAlertsBadge,
    fraudAlerts: fraudAlertsBadge,
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Barre mobile (Lot A) : ouvre la sidebar en tiroir sous md. */}
      <div className="md:hidden sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background px-4">
        <button type="button" aria-label="Ouvrir le menu" onClick={() => setMobileNavOpen(true)} className="p-1.5 rounded-md hover:bg-muted">
          <Menu className="h-5 w-5" />
        </button>
        <span className="font-semibold text-sm">Constrigo Ops</span>
      </div>
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMobileNavOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-sidebar border-r border-sidebar-border transition-transform md:translate-x-0",
          mobileNavOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Shield className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Constrigo Ops</div>
              <div className="text-xs text-muted-foreground">Centre opérationnel</div>
            </div>
            {/* Indicateur de rôle (pas une autorisation) : « Lecture seule »
                était devenu faux — SUPPORT écrit dans le workflow tickets. */}
            {user?.staffRole === "ADMIN_SUPPORT" && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0.5 shrink-0"
                title="Lecture du backoffice · écriture autorisée dans Support"
              >
                Accès limité
              </Badge>
            )}
            {/* Notification bell — notifications OPS : jamais pour un freelance. */}
            {!isFreelance && (
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                className="relative p-1.5 rounded-md hover:bg-sidebar-accent/50 transition-colors"
                onClick={() => setNotifOpen((v) => !v)}
                title="Notifications OPS"
              >
                <Bell className="h-4 w-4 text-muted-foreground" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-white px-1">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute left-0 top-10 z-50 w-80 rounded-lg border bg-popover shadow-lg">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="text-sm font-medium">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={markAllRead}
                      >
                        Tout marquer comme lu
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="py-6 text-center text-xs text-muted-foreground">
                        Aucune notification
                      </div>
                    ) : (
                      notifications.slice(0, 20).map((n) => (
                        <button
                          type="button"
                          key={n.id}
                          className={cn(
                            "w-full text-left px-3 py-2 text-xs border-b last:border-0 hover:bg-muted/50 transition-colors",
                            !n.read && "bg-primary/5"
                          )}
                          onClick={() => {
                            if (n.link) navigate(n.link);
                            setNotifOpen(false);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                "inline-block h-2 w-2 rounded-full shrink-0",
                                n.type === "NEW_DISPUTE" && "bg-red-500",
                                n.type === "MESSAGE_FLAGGED" && "bg-orange-500",
                                n.type === "TRIAL_J3" && "bg-yellow-500",
                                n.type === "PENDING_ARTISAN" && "bg-blue-500",
                                n.type === "REFUND_CLAIM" && "bg-orange-500",
                                n.type === "TENDER_INTERESTED" && "bg-sky-500",
                                n.type === "AO_DOSSIER_CONFIE" && "bg-amber-500",
                                n.type === "FREELANCE_2FA_ENROLLED" && "bg-red-500",
                              )}
                            />
                            <span className="font-medium truncate">
                              {n.type === "NEW_DISPUTE" && "Litige"}
                              {n.type === "MESSAGE_FLAGGED" && "Message suspect"}
                              {n.type === "TRIAL_J3" && "Trial J-3"}
                              {n.type === "PENDING_ARTISAN" && "Artisan en attente"}
                              {n.type === "REFUND_CLAIM" && "Remboursement"}
                              {n.type === "TENDER_INTERESTED" && "Client intéressé par un AO"}
                              {n.type === "AO_DOSSIER_CONFIE" && "Dossier AO confié — à facturer"}
                              {n.type === "FREELANCE_2FA_ENROLLED" && "2FA activée par un freelance"}
                            </span>
                            <span className="ml-auto text-muted-foreground shrink-0">
                              {new Date(n.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                          {(n.companyName || n.name || n.reference) && (
                            <div className="text-muted-foreground mt-0.5 truncate">
                              {n.companyName || n.name || n.reference}
                            </div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Search */}
          <div className="p-4 border-b border-sidebar-border space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Naviguer dans le menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {!isFreelance && (
            <button
              type="button"
              onClick={() => navigate(searchQuery.trim().length >= 2 ? `/search?q=${encodeURIComponent(searchQuery.trim())}` : "/search")}
              className="w-full text-xs text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/40 rounded px-2 py-1 text-left"
              title="Recherche globale (artisan, particulier, projet, entreprise)"
            >
              🔎 Recherche globale (Cmd+K)
            </button>
            )}
          </div>

          <SidebarNav
            groups={navGroups}
            role={user?.staffRole}
            userKey={user?.email || "anonymous"}
            pathname={location.pathname}
            searchQuery={searchQuery}
          />

          {/* User section */}
          <div className="border-t border-sidebar-border p-4">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                {user?.email?.[0]?.toUpperCase() || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.email}</div>
                <div className="text-xs text-muted-foreground">
                  {user?.staffRole === "SUPER_ADMIN" && "Super Admin"}
                  {user?.staffRole === "ADMIN_OPS" && "Admin OPS"}
                  {user?.staffRole === "ADMIN_SALES" && "Admin Commercial"}
                  {user?.staffRole === "ADMIN_SUPPORT" && "Observateur"}
                  {user?.staffRole === "FREELANCE_SDR" && "SDR freelance"}
                  {user?.staffRole === "FREELANCE_SALES" && "Sales freelance"}
                </div>
              </div>
            </div>
            {canAccessPage(user?.staffRole, "MY_ACCOUNT") && (
              <Button asChild variant="ghost" className="w-full justify-start">
                <Link to="/mon-compte">
                  <KeyRound className="h-4 w-4 mr-2" />
                  Mon compte
                </Link>
              </Button>
            )}
            <Button variant="ghost" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:ml-64">
        <main key={location.pathname} className="p-4 md:p-8 animate-slide-up">
          <RouteErrorBoundary>{children}</RouteErrorBoundary>
        </main>
      </div>

      {/* Recherche globale (admin.search.global) : hors périmètre des freelances. */}
      {!isFreelance && <CommandPalette />}
    </div>
  );
}

